import { accountKeys, type AccountClass } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useQueries } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { useSession } from '@/app/session'
import { otherLedgerTargets, type Target } from '../internal/targets.ts'

export interface UseOtherLedgerAccountsOptions {
  readonly activeLedgerId: () => string | undefined
  readonly classes: () => readonly AccountClass[]
  readonly locale: () => Locale
  readonly enabled: () => boolean
}

export interface UseOtherLedgerAccounts {
  readonly targets: ComputedRef<readonly Target[]>
  readonly isLoading: ComputedRef<boolean>
}

/** Every other non-archived ledger the signed-in subject can post into (AGENTS.md's cross-ledger
 * picker: `TRANSFER` resolved via the role in that ledger, not the active one). */
export function useOtherLedgerAccounts(options: UseOtherLedgerAccountsOptions): UseOtherLedgerAccounts {
  const api = useApi()
  const session = useSession()

  const otherLedgers = computed(() =>
    options.enabled()
      ? session.ledgers.value.filter(
          (ledger) =>
            ledger.id !== options.activeLedgerId() &&
            ledger.archivedAt === undefined &&
            ledger.capabilities.includes('TRANSFER'),
        )
      : [],
  )

  const treeQueries = useQueries({
    queries: computed(() =>
      otherLedgers.value.map((ledger) => ({
        queryKey: accountKeys.tree(ledger.id),
        queryFn: ({ signal }: { signal?: AbortSignal }) => api.accounts.tree(ledger.id, signal),
        retry: false,
      })),
    ),
  })

  const targets = computed(() =>
    otherLedgers.value.flatMap((ledger, index) =>
      otherLedgerTargets(ledger, treeQueries.value[index]?.data ?? [], options.classes(), options.locale()),
    ),
  )

  return {
    targets,
    isLoading: computed(
      () => otherLedgers.value.length > 0 && treeQueries.value.some((query) => query.isPending),
    ),
  }
}
